import React, { useState, useEffect } from "react"

import { Button, Modal, Form } from "react-bootstrap"
import { useIntl } from "react-intl"

import { getObjectsList, addNewObject } from "../../Loaders"
import { logToServer } from "../../ErrorBoundary"
import { Required } from "../../shared_components/widgets/utils"
import { getLocaleFormattedUserString, RequiredFieldWarning } from "../internationalization/LocaleFormatted"
import UserRequestTable from "./UserRequestTable"
import ToggleSwitch from "../fermmanagement/modals/StartRunModal/ToggleSwitch"
import ReactSelectionElement from "../../shared_components/widgets/ReactSelectionElement"
import { withAppSettingsConfig } from "../../config/AppSettingsConfigProvider"

function UserRequestModal({
  operator,
  activeUsers, // AppSettingsConfigProvider
}) {
  const intl = useIntl()
  const [showModal, setShowModal] = useState(false)
  const [showRequestTable, setShowRequestTable] = useState(false)
  const [actorOptions, setActorOptions] = useState({})
  const [actor, setActor] = useState(operator || "")
  const [urgencyLevelOptions, setUrgencyLevelOptions] = useState([])
  const [urgency, setUrgency] = useState("Low")
  const [description, setDescription] = useState("")
  const [error, setError] = useState(false)
  const [warning, setWarning] = useState(false)

  useEffect(() => {
    setActor(operator)
  }, [operator])

  useEffect(() => {
    setActorOptions(Object.fromEntries(activeUsers.map((user) => [user.username, user])))
  }, [activeUsers])

  useEffect(() => {
    if (!showModal) {
      return
    }

    // Get urgency level options
    getObjectsList("/users/user-request/urgency_level/")
      .then((urgencyLevel) => {
        setUrgencyLevelOptions(urgencyLevel)
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        setError(true)
      })
  }, [showModal])

  const handleChange = (event) => {
    switch (event.target.name) {
      case "description":
        setDescription(event.target.value)
        break
      case "urgency":
        setUrgency(event.target.value)
        break
      case "actor":
        setActor(event.target.value)
        break
      default:
        break
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    // Make sure user entered description
    if (!description) {
      setWarning(true)
    } else {
      const formState = { description, urgency, actor }

      addNewObject("/users/user-request/", formState)
        .then((_) => {
          // If request is successful, reset the form and close the modal
          resetFormState()
          setShowModal(false)
          setShowRequestTable(false)

          // Use setTimeout to delay the alert
          setTimeout(() => {
            alert(
              intl.formatMessage({
                id: "general.request.submitted",
                defaultMessage: "Your request has been submitted",
              }),
            )
          }, 1000) // Delay of 1 seconds (1000 ms)
        })
        .catch((err) => {
          console.error(err)
          logToServer(err)
          setError(true)
        })
    }
  }

  const resetFormState = () => {
    setDescription("")
    setUrgency("Low")
    setActor(operator || "")
    setError(false)
    setWarning(false)
  }

  const currentActor = Object.values(actorOptions).find((o) => actor === o.username) || {}

  return (
    <>
      <Button
        variant={"outline-primary"}
        size={"sm"}
        style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", height: "31px" }}
        title={intl.formatMessage({ id: "general.request.form", defaultMessage: "User Request Form" })}
        onClick={() => setShowModal(true)}
      >
        <span className={"fa fa-regular fa-bell"} />
      </Button>

      {showModal && (
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false)
            resetFormState()
            setShowRequestTable(false)
          }}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {intl.formatMessage({ id: "general.request.form", defaultMessage: "User Request Form" })}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error ? (
              <p className={"error-message"}>
                {intl.formatMessage({
                  id: "general.server.error",
                  defaultMessage: "Error! Could not process request",
                })}
              </p>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Form.Group>
                  <Form.Label>
                    {intl.formatMessage({ id: "general.description", defaultMessage: "Description" })}
                  </Form.Label>
                  {<Required />}
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder={intl.formatMessage({ id: "general.details", defaultMessage: "Details" })}
                    name="description"
                    value={description}
                    onChange={handleChange}
                  />
                  {warning && <RequiredFieldWarning warning={warning} />}
                </Form.Group>
                <ReactSelectionElement
                  name={"urgency"}
                  label={intl.formatMessage({
                    id: "general.request.form.urgency",
                    defaultMessage: "Urgency Level",
                  })}
                  list={urgencyLevelOptions.map((u) => {
                    return { value: u }
                  })}
                  value={{ value: urgency || "" }}
                  onValueChange={(_, selection) => setUrgency(selection?.value)}
                  isMulti={false}
                  isClearable={false}
                  divStyle={{ marginTop: "1rem" }}
                />
                <ReactSelectionElement
                  name={"actor"}
                  label={intl.formatMessage({ id: "general.actor", defaultMessage: "Actor" })}
                  getOptionLabel={(obj) => obj.label}
                  list={Object.values(actorOptions).map((a) => {
                    return { value: a.username, label: getLocaleFormattedUserString(a, intl) }
                  })}
                  value={{
                    value: currentActor?.username || "",
                    label: getLocaleFormattedUserString(currentActor, intl),
                  }}
                  onValueChange={(_, selection) => setActor(selection?.value)}
                  isMulti={false}
                  isClearable={false}
                />
                <Button
                  variant="outline-primary"
                  type="button" // Ensure that clicking this button won't submit the form
                  style={{ marginRight: "10px" }}
                  onClick={resetFormState}
                >
                  {intl.formatMessage({ id: "general.clearButton", defaultMessage: "Clear" })}
                </Button>
                <Button variant="primary" type="submit">
                  {intl.formatMessage({ id: "general.submitButton", defaultMessage: "Submit" })}
                </Button>
              </Form>
            )}

            <ToggleSwitch
              intl={intl}
              idVal={"userRequestTableSwitch"}
              value={showRequestTable}
              show={true}
              labelInfo={{
                id: "general.request.table.show",
                defaultMessage: "Show previous requests",
              }}
              onToggle={() => setShowRequestTable(!showRequestTable)}
              toggleStyle={{ marginTop: "1rem" }}
            />

            {showRequestTable && <UserRequestTable intl={intl} />}
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

export default withAppSettingsConfig(UserRequestModal)
